import React, { useEffect, useRef, useState } from "react";
import { FileInput } from "lucide-react";
import "../../../../../../styles/documents/modals/pdf-modal.css";
import { LinkPicker, LinkOption } from "../../../../../atoms";
import api from "../../../../../../utils/api";
import { API_ENDPOINTS } from "../../../../../../config";
import { DOCUMENT_CATEGORIES } from "../../../constants";
import { useIsMobile } from "../../../../../../hooks/useMediaQuery";

export type PdfDocData = {
  title: string;
  category: string;
  description: string;
  boardLinked?: string;
  jobLinked?: string;
  file?: File;
};

interface Kanban {
  uid: string;
  name: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name?: string;
}

export interface PdfDocumentModalProps {
  open: boolean;
  onClose: () => void;
  savedValue: PdfDocData;
  onCommit: (v: PdfDocData) => void;
  pdfUrl?: string;
}

const DEFAULT_PDF_URL = "";

export default function PdfDocumentModal({
  open,
  onClose,
  savedValue,
  onCommit,
  pdfUrl = DEFAULT_PDF_URL,
}: PdfDocumentModalProps) {
  const isMobile = useIsMobile();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Resume');
  const [description, setDescription] = useState('');

  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pdfError, setPdfError] = useState(false);
  const [boards, setBoards] = useState<LinkOption[]>([]);
  const [jobs, setJobs] = useState<LinkOption[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [boardLinked, setBoardLinked] = useState<string | undefined>();
  const [jobLinked, setJobLinked] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setTitle(savedValue.title || '');
    setCategory(savedValue.category || 'Resume');
    setDescription(savedValue.description || '');
    setUploadedPdfUrl(null);
    setUploadedFile(null);
    setPdfError(false);
    setBoardLinked(savedValue.boardLinked);
    setJobLinked(savedValue.jobLinked);
  }, [open, savedValue]);

  useEffect(() => {
    if (open) {
      fetchBoards();
    }
  }, [open]);

  useEffect(() => {
    if (boardLinked) {
      fetchJobs(boardLinked);
    } else {
      setJobs([]);
      setJobLinked(undefined);
    }
  }, [boardLinked]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const data = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
      const boardOptions: LinkOption[] = data.map(kanban => ({
        id: kanban.uid,
        label: kanban.name
      }));
      setBoards(boardOptions);
    } catch (error) {

      setBoards([]);
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchJobs = async (kanbanUid: string) => {
    try {
      setLoadingJobs(true);
      const data = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanbanUid));
      const jobOptions: LinkOption[] = data.map(job => ({
        id: job.uid,
        label: job.company_name ? `${job.job_title} at ${job.company_name}` : job.job_title
      }));
      setJobs(jobOptions);
    } catch (error) {

      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleBoardChange = (boardId: string | null) => {
    setBoardLinked(boardId || undefined);
    setJobLinked(undefined);
  };

  const handleJobChange = (jobId: string | null) => {
    setJobLinked(jobId || undefined);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (catRef.current && !catRef.current.contains(t)) setCatOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const isDirty =
    title.trim() !== '' ||
    description.trim() !== '' ||
    uploadedFile !== null;

  const handleCreate = () => {
    if (!isDirty || !uploadedFile) return;
    onCommit({ title, category, description, boardLinked, jobLinked, file: uploadedFile });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'];
      if (allowedTypes.includes(file.type)) {
        const url = URL.createObjectURL(file);
        setUploadedPdfUrl(url);
        setUploadedFile(file);
        if (!title) {
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
          setTitle(nameWithoutExt);
        }
      } else {

      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (!open) return null;

  const createButton = (
    <button
      type="button"
      className={`pdm-btn pdm-btn--primary ${isDirty ? "" : "pdm-btn-disabled"}`}
      onClick={handleCreate}
      disabled={!isDirty}
    >
      Create
    </button>
  );

  return (
    <div className="pdm-overlay">
      <div className="pdm-overlay-bg" onClick={onClose} />
      <div className="pdm-modal" role="dialog" aria-modal="true">
        <div className="pdm-grid">
          <div className="pdm-left">
            <div className="pdm-toolbar">
              <div className="pdm-toolbar-title">Document Preview</div>
              <div className="pdm-toolbar-actions">
                <button
                  type="button"
                  className="pdm-btn pdm-btn--close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="pdm-divider" />

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            <div className="pdm-pdfbox" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {uploadedPdfUrl ? (
                pdfError ? (
                  <div className="pdm-pdf-fallback">Preview not available</div>
                ) : (
                  <div className="pdm-iframe-wrap">
                    <div className="pdm-iframe-inner">
                      <iframe
                        title="pdf"
                        src={`${uploadedPdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        className="pdm-iframe"
                        onError={() => setPdfError(true)}
                      />
                    </div>
                  </div>
                )
              ) : (
                <div 
                  className="pdm-empty-state"
                  onClick={handleUploadClick}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '40px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '48px', marginBottom: '16px', color: '#9ca3af' }}>
                    <FileInput size={48} />
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#1f2937' }}>
                    Upload PDF Document
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                    Click here or drag and drop your PDF file
                  </div>
                  <button 
                    type="button"
                    className="pdm-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUploadClick();
                    }}
                    style={{ marginTop: '8px' }}
                  >
                    Choose File
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="pdm-right">
            <div className="pdm-right-top">
              <div className="pdm-right-title">Create Document</div>
              {!isMobile && createButton}
            </div>

            <div className="pdm-divider" />

            <label className="pdm-label">
              Title <span className="pdm-label-hint">Required</span>
            </label>
            <input
              className="pdm-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <label className="pdm-label">
              Document Category <span className="pdm-label-hint">Required</span>
            </label>
            <div className="pdm-select" ref={catRef}>
              <button
                type="button"
                className="pdm-select-trigger"
                onClick={() => setCatOpen((v) => !v)}
              >
                <span className="pdm-select-value">{category}</span>
                <span className="pdm-select-caret">▾</span>
              </button>

              {catOpen && (
                <div className="pdm-dropdown">
                  {DOCUMENT_CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="pdm-dropdown-item"
                      onClick={() => {
                        setCategory(c);
                        setCatOpen(false);
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="pdm-label">Description</label>
            <textarea
              className="pdm-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ marginBottom: '12px' }}
            />

            <LinkPicker
              label="Boards"
              placeholder={loadingBoards ? "Loading boards..." : "Link board"}
              options={boards}
              value={boardLinked || null}
              onChange={handleBoardChange}
              disabled={loadingBoards}
            />

            <div style={{ marginTop: '12px' }}>
              <LinkPicker
                label="Jobs"
                placeholder={
                  !boardLinked 
                    ? "Select a board first" 
                    : loadingJobs 
                      ? "Loading jobs..." 
                      : "Link job"
                }
                options={jobs}
                value={jobLinked || null}
                onChange={handleJobChange}
                disabled={!boardLinked || loadingJobs}
              />
            </div>

            {isMobile && (
              <div className="pdm-sheet-actions">{createButton}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}